/**
 * EditorDirectionToggle - LTR/RTL paragraph-direction control for the rich
 * text toolbars. Mirrors the alignment ToggleGroup: nothing is highlighted
 * while a block uses the default `auto` direction; picking a side forces it,
 * and clicking the active side again returns the block to auto-detection.
 */
import { useTranslation } from 'react-i18next';
import { PilcrowLeft, PilcrowRight } from 'lucide-react';
import type { Editor } from '@tiptap/react';
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group';

interface EditorDirectionToggleProps {
  editor: Editor;
  /** 'sm' matches the compact toolbar's 7x7 buttons. */
  size?: 'default' | 'sm';
}

export function EditorDirectionToggle({ editor, size = 'default' }: EditorDirectionToggleProps) {
  const { t } = useTranslation('common');
  const itemClassName = size === 'sm' ? 'h-7 w-7 p-0' : undefined;
  const iconClassName = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  // `toggleTextDirection` reads the editor's live state on each click (applies the
  // direction, or resets to the default `auto` if it's already active), so it works
  // even though this toolbar doesn't re-render on every editor transaction. The
  // `data-state` highlight mirrors the sibling alignment ToggleGroup pattern.
  return (
    <ToggleGroup type="single" size="sm">
      <ToggleGroupItem
        value="ltr"
        className={itemClassName}
        aria-label={t('editor.textDirectionLtr')}
        title={t('editor.textDirectionLtr')}
        onClick={() => editor.chain().focus().toggleTextDirection('ltr').run()}
        data-state={editor.isActive({ dir: 'ltr' }) ? 'on' : 'off'}
      >
        <PilcrowLeft className={iconClassName} />
      </ToggleGroupItem>
      <ToggleGroupItem
        value="rtl"
        className={itemClassName}
        aria-label={t('editor.textDirectionRtl')}
        title={t('editor.textDirectionRtl')}
        onClick={() => editor.chain().focus().toggleTextDirection('rtl').run()}
        data-state={editor.isActive({ dir: 'rtl' }) ? 'on' : 'off'}
      >
        <PilcrowRight className={iconClassName} />
      </ToggleGroupItem>
    </ToggleGroup>
  );
}
